/** @jsxImportSource @emotion/react */
import { formatDate, formatUsd, kaiPartialIdPresenter } from '@kaiizen/kai-presenters';
import type { ApiActivityLog, ApiScopeChangeGroupChange, ApiScopeChangeTaskChange } from '@kaiizen/kai-pro-api';
import { useState } from 'react';

import { ACTIVITY_LOG_SEPARATOR, getActorLabel } from '@/Client/Lib';
import { useNotesDrawerOptional, useProjectActivityLog, useShowNotes } from '@/Client/State';
import { useNavigateToScope } from '@/Client/State/kaiNavigation';
import type { FocusedProjectOverviewPanelStyles } from './focusedProjectOverviewPanelStyles';

const ACTIVITY_PREVIEW_LIMIT = 3;

const getTaskCountLabel = (count: number) => `${count} ${count === 1 ? 'task' : 'tasks'}`;
const getGroupCountLabel = (count: number) => `${count} ${count === 1 ? 'group' : 'groups'}`;

const getScopeChangeSummary = (entry: ApiActivityLog) => entry.scopeChangeSummary ?? entry.handoffSummary;
const isApprovalActivity = (entry: ApiActivityLog) => entry.eventType === 'scopeApproved' || entry.eventType === 'changeOrderApproved';

type ActivityWorkPeriod = {
  actorKey: string;
  entries: ApiActivityLog[];
  id: string;
  workPeriod: number;
};

/**
 * Keeps one person's contiguous work — including the handoff or take-work that
 * ends or begins it — in a single, expandable visual period. A later return by
 * the same person deliberately starts a new period.
 */
const getActivityWorkPeriods = (entries: ApiActivityLog[]): ActivityWorkPeriod[] => {
  const workPeriods: ActivityWorkPeriod[] = [];

  for (const entry of entries) {
    const actorKey = entry.actorUserId ?? entry.actorName ?? entry.id;
    const previousWorkPeriod = workPeriods[workPeriods.length - 1];
    if (previousWorkPeriod?.actorKey === actorKey) {
      previousWorkPeriod.entries.push(entry);
      continue;
    }
    workPeriods.push({ actorKey, entries: [entry], id: entry.id, workPeriod: workPeriods.length });
  }

  return workPeriods;
};

const getScopeChangeSummaryLabel = (groups: ApiScopeChangeGroupChange[]): string => {
  if (groups.length === 0) return 'Updated scope';
  const tasks = groups.flatMap(group => group.tasks);
  const actionLabels = (['added', 'edited', 'removed'] as const)
    .map(action => ({ action, count: tasks.filter(task => task.action === action).length }))
    .filter(({ count }) => count > 0)
    .map(({ action, count }) => `${action[0]!.toUpperCase()}${action.slice(1)} ${getTaskCountLabel(count)}`);
  return `${actionLabels.join(', ')} · ${getGroupCountLabel(groups.length)}`;
};

const getActivityLabel = (entry: ApiActivityLog): string => {
  const summary = getScopeChangeSummary(entry);
  if (summary) return getScopeChangeSummaryLabel(summary.groups);
  if (isApprovalActivity(entry)) return 'Approved';
  if (entry.eventType === 'projectHandedOff') return 'Handed off project';
  if (entry.eventType === 'projectWorkTaken') return 'Took work';
  return 'Updated scope';
};

const getTurnAnnotation = (entry: ApiActivityLog): string | undefined => {
  if (!entry.aboutUserName) return undefined;
  if (entry.eventType === 'projectHandedOff') return `Handed off to ${entry.aboutUserName}`;
  if (entry.eventType === 'projectWorkTaken') return `Took work from ${entry.aboutUserName}`;
  return undefined;
};

const getWorkPeriodAnnotation = (entries: ApiActivityLog[]): string | undefined =>
  entries
    .map(getTurnAnnotation)
    .find(annotation => annotation != null);

const getWorkPeriodNote = (entries: ApiActivityLog[]): string | undefined =>
  entries
    .map(entry => entry.byUserComment ?? entry.details)
    .find(note => note != null);

/** Merges all task changes from one uninterrupted work period by scope group. */
const getWorkPeriodScopeChangeGroups = (entries: ApiActivityLog[]): ApiScopeChangeGroupChange[] => {
  const groupsById = new Map<string, ApiScopeChangeGroupChange>();

  for (const entry of entries) {
    for (const group of getScopeChangeSummary(entry)?.groups ?? []) {
      const existingGroup = groupsById.get(group.id);
      if (existingGroup) {
        existingGroup.tasks.push(...group.tasks);
      } else {
        groupsById.set(group.id, { ...group, tasks: [...group.tasks] });
      }
    }
  }

  return [...groupsById.values()];
};

const getWorkPeriodActivityLabel = (entries: ApiActivityLog[], groups: ApiScopeChangeGroupChange[]): string => {
  const approvalLabel = entries.some(isApprovalActivity) ? 'Approved' : undefined;
  if (groups.length > 0) {
    const scopeChangeLabel = getScopeChangeSummaryLabel(groups);
    return approvalLabel ? `${scopeChangeLabel} · ${approvalLabel}` : scopeChangeLabel;
  }
  if (approvalLabel) return approvalLabel;
  return getActivityLabel(entries[0]!);
};

const getDifferenceLabel = (previousTotalAmount: number | undefined, totalAmount: number | undefined): string => {
  if (previousTotalAmount == null || totalAmount == null) return '—';
  const difference = totalAmount - previousTotalAmount;
  return `${difference > 0 ? '+' : ''}${formatUsd(difference)}`;
};

const getDifferenceStyle = (previousTotalAmount: number | undefined, totalAmount: number | undefined, styles: FocusedProjectOverviewPanelStyles) => {
  if (previousTotalAmount == null || totalAmount == null) return styles.activityAmount;
  if (totalAmount > previousTotalAmount) return [styles.activityAmount, styles.activityIncrease];
  if (totalAmount < previousTotalAmount) return [styles.activityAmount, styles.activityDecrease];
  return styles.activityAmount;
};

const getArtifactLabel = (entry: ApiActivityLog): string => entry.eventType === 'changeOrderApproved' ? 'Change order' : 'Scope';

/** Formats one task change as the compact, dot-separated detail shown in an activity row. */
const getTaskChangeDescription = (task: ApiScopeChangeTaskChange): string =>
  `${task.action[0]!.toUpperCase()}${task.action.slice(1)} task${task.totalAmount == null ? '' : `${ACTIVITY_LOG_SEPARATOR}${formatUsd(task.totalAmount)}`}`;

/** Renders all task-level work captured during one uninterrupted work period. */
const ScopeChangeDetails = ({ groups, styles }: { groups: ApiScopeChangeGroupChange[]; styles: FocusedProjectOverviewPanelStyles }) => {
  return (
    <div css={styles.activityDetails}>
      {groups.map(group => (
        <div css={styles.activityDetailGroup} key={group.id}>
          <strong css={styles.activityDetailGroupName}>{group.name}</strong>
          <ul css={styles.activityDetailTaskList}>
            {group.tasks.map((task, index) => (
              <li css={styles.activityTaskChange} key={`${task.id}-${index}`}>
                <span css={styles.activityTaskId}>{kaiPartialIdPresenter(task.id)}</span>
                <span css={styles.activityTaskSummary}>
                  <span css={styles.activityTaskName}>{task.name}</span>
                  <span css={styles.activityTaskDescription}>
                    {getTaskChangeDescription(task)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

/** Shows the recorded scope changes and the turn events that annotate them. */
export const ProjectOverviewActivity = ({ projectId, styles }: {
  projectId: string;
  styles: FocusedProjectOverviewPanelStyles;
}) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedWorkPeriodId, setExpandedWorkPeriodId] = useState<string>();
  const { showNotesEditor } = useShowNotes();
  const notesDrawer = useNotesDrawerOptional();
  const navigateToScope = useNavigateToScope();
  const { entries, hasMore, isLoading, isLoadingMore, loadMore } = useProjectActivityLog(projectId, { limit: 20 });
  const activityEntries = entries.filter(entry =>
    entry.eventType === 'walkUpdated'
    || entry.eventType === 'projectHandedOff'
    || entry.eventType === 'projectWorkTaken'
    || isApprovalActivity(entry)
  );
  const activityWorkPeriods = getActivityWorkPeriods(activityEntries);
  const visibleWorkPeriods = showAll ? activityWorkPeriods : activityWorkPeriods.slice(0, ACTIVITY_PREVIEW_LIMIT);
  const hiddenWorkPeriodCount = activityWorkPeriods.length - ACTIVITY_PREVIEW_LIMIT;
  const hasHiddenWorkPeriods = hiddenWorkPeriodCount > 0;
  const openNotes = () => {
    if (notesDrawer) notesDrawer.openNotesDrawer({ showScopeNotes: true });
    else showNotesEditor({});
  };

  return (
    <section css={styles.activityModule} aria-labelledby='project-overview-activity'>
      <div css={styles.moduleHeader}>
        <h2 id='project-overview-activity' css={styles.moduleTitle}>Activity</h2>
      </div>
      {!isLoading && activityEntries.length === 0
        ? <div css={styles.moduleEmpty}>No activity yet</div>
        : (
          <div css={styles.activityTable}>
            <div css={styles.activityTableHeader}>
              <span>Date</span>
              <span aria-label='Activity marker' />
              <span>Who</span>
              <span>What</span>
              <span>Artifact</span>
              <span>Note</span>
              <span css={styles.activityAmountsHeader}>
                <span>Difference</span>
                <span>Total</span>
              </span>
            </div>
            {visibleWorkPeriods.map(workPeriod => {
              const latestEntry = workPeriod.entries[0]!;
              const earliestEntry = workPeriod.entries[workPeriod.entries.length - 1]!;
              const isExpanded = expandedWorkPeriodId === workPeriod.id;
              const actorLabel = getActorLabel(latestEntry);
              const turnAnnotation = getWorkPeriodAnnotation(workPeriod.entries);
              const workPeriodNote = getWorkPeriodNote(workPeriod.entries);
              const scopeChangeGroups = getWorkPeriodScopeChangeGroups(workPeriod.entries);
              const activityLabel = getWorkPeriodActivityLabel(workPeriod.entries, scopeChangeGroups);
              const hasDetails = scopeChangeGroups.length > 0;
              return (
                <div
                  css={[styles.activityPeriod, workPeriod.workPeriod % 2 === 1 && styles.activityPeriodAlternate]}
                  data-activity-period={workPeriod.workPeriod}
                  key={workPeriod.id}
                >
                  <div css={styles.activityRow}>
                    <span>{formatDate(latestEntry.createdAt)}</span>
                    <span css={styles.activityMarker} aria-hidden='true'>
                      <span css={styles.activityMarkerDot} />
                    </span>
                    <span>{actorLabel}</span>
                    {hasDetails
                      ? (
                        <button
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} ${activityLabel} from ${actorLabel}`}
                          css={styles.activityWhatButton}
                          type='button'
                          onClick={() => setExpandedWorkPeriodId(current => current === workPeriod.id ? undefined : workPeriod.id)}
                        >
                          <span>{activityLabel}</span>
                          <span css={styles.activityCaret}>{isExpanded ? '▴' : '▾'}</span>
                          {turnAnnotation && <span css={styles.activityRecipient}>{turnAnnotation}</span>}
                        </button>
                      )
                      : (
                        <span css={styles.activityWhat}>
                          <span>{activityLabel}</span>
                          {turnAnnotation && <span css={styles.activityRecipient}>{turnAnnotation}</span>}
                        </span>
                      )}
                    <span css={styles.activityArtifact}>
                      {latestEntry.scanId
                        ? (
                          <button css={styles.activityArtifactButton} type='button' onClick={() => navigateToScope({ scanId: latestEntry.scanId! })}>
                            {getArtifactLabel(latestEntry)}
                          </button>
                        )
                        : <span css={styles.activityArtifactLabel}>{getArtifactLabel(latestEntry)}</span>}
                      {isApprovalActivity(latestEntry) && <span css={styles.activityArtifactStatus}>Approved</span>}
                    </span>
                    <span css={styles.activityNote}>
                      {workPeriodNote
                        ? <button type='button' css={styles.activityNoteButton} onClick={openNotes}>{workPeriodNote}</button>
                        : '—'}
                    </span>
                    <span css={styles.activityAmounts}>
                      <span css={getDifferenceStyle(earliestEntry.previousTotalAmount, latestEntry.totalAmount, styles)}>
                        {getDifferenceLabel(earliestEntry.previousTotalAmount, latestEntry.totalAmount)}
                      </span>
                      <span css={styles.activityAmount}>{latestEntry.totalAmount == null ? '—' : `→ ${formatUsd(latestEntry.totalAmount)}`}</span>
                    </span>
                  </div>
                  {isExpanded && <ScopeChangeDetails groups={scopeChangeGroups} styles={styles} />}
                </div>
              );
            })}
            {hasHiddenWorkPeriods && (
              <button
                aria-expanded={showAll}
                css={styles.activityMore}
                type='button'
                onClick={() => setShowAll(current => !current)}
              >
                {showAll
                  ? 'Hide earlier activity'
                  : `View all history (${hiddenWorkPeriodCount} earlier ${hiddenWorkPeriodCount === 1 ? 'period' : 'periods'})`}
              </button>
            )}
            {hasMore && (
              <button
                css={styles.activityMore}
                type='button'
                disabled={isLoadingMore}
                onClick={() => {
                  setShowAll(true);
                  void loadMore();
                }}
              >
                {isLoadingMore ? 'Loading history…' : 'Load more history'}
              </button>
            )}
          </div>
        )}
    </section>
  );
};
